import { type ColumnDef, type VisibilityState } from '@tanstack/react-table';

import type { OpsOrder } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { OrderColumnHeader } from './ColumnHeader';
import { EventRowActions } from './UnifiedRowActions';
import {
  selectColumn,
  statusColumn,
  dateColumn,
  warehouseColumn,
  actionsColumn,
} from './sharedColumns';

export const eventOrderColumns: ColumnDef<OpsOrder>[] = [
  selectColumn(),
  statusColumn(),
  {
    id: 'eventAccount',
    meta: {
      get title() {
        return i18n.t('Event:eventAccount');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Event:eventAccount')} />
    ),
    accessorFn: (row) => row.event_account?.name ?? '',
    cell: ({ row }) => {
      const name = row.original.event_account?.name || '-';
      const itemLocationsRow = row.original.item_locations
        .map((l) => l.name)
        .join(', ');

      return (
        <div className="tw:flex tw:flex-col tw:max-w-[200px]">
          <span className="tw:font-medium tw:truncate">{name}</span>
          {itemLocationsRow && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="tw:text-muted-foreground tw:text-xs tw:truncate">
                  {itemLocationsRow}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">
                {row.original.item_locations.map((l) => (
                  <div key={l.id}>{l.name}</div>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    id: 'amount',
    meta: {
      get title() {
        return i18n.t('Order:columnAmount');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader column={column} title={i18n.t('Order:columnAmount')} />
    ),
    accessorFn: (row) => row.total_amount ?? 0,
    cell: ({ row }) => {
      const boxCounts = row.original.box_counts ?? [];
      const boxLine = boxCounts
        .filter((b) => b.count > 0)
        .sort((a, b) => a.box_size.localeCompare(b.box_size))
        .map((b) => `${b.count} x ${b.box_size}`)
        .join(' / ');

      return (
        <div className="tw:flex tw:flex-col tw:min-w-[100px]">
          <span className="tw:text-sm tw:font-medium tw:tabular-nums">
            {row.original.total_amount}
          </span>
          {boxLine && (
            <span className="tw:text-muted-foreground tw:text-sm">
              {boxLine}
            </span>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
  dateColumn({
    id: 'deliveryDate',
    titleKey: 'Order:columnDeliveryDate',
    accessorKey: 'delivery_date',
  }),
  dateColumn({
    id: 'carrierPickupDate',
    titleKey: 'Event:carrierPickupDate',
    accessorKey: 'carrier_pickup_date',
  }),
  {
    id: 'deliveryAddress',
    meta: {
      get title() {
        return i18n.t('Order:columnDeliveryAddress');
      },
    },
    header: ({ column }) => (
      <OrderColumnHeader
        column={column}
        title={i18n.t('Order:columnDeliveryAddress')}
      />
    ),
    accessorFn: (row) => row.delivery_address?.streetname ?? '-',
    cell: ({ row }) => {
      const addr = row.original.delivery_address;
      if (!addr) return '-';
      const street = [addr.streetname, addr.streetno].filter(Boolean).join(' ');
      const extra = addr.additional_info;
      return (
        <div className="tw:flex tw:flex-col tw:max-w-[200px]">
          <span className="tw:text-sm tw:font-medium tw:truncate">
            {street || '-'}
          </span>
          {extra && (
            <span className="tw:text-sm tw:text-muted-foreground tw:truncate">
              {extra}
            </span>
          )}
        </div>
      );
    },
  },
  warehouseColumn('Order:columnOriginWarehouse'),
  actionsColumn(EventRowActions),
];

export const eventDefaultHiddenColumns: VisibilityState = {
  itemLocation: false,
  warehouse: false,
};
